import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { OUTREACH_PRESENCE_HEARTBEAT_MS } from '@aegislab/shared';
import { fetchOutreachPresence, renewOutreachPresence } from '../api/outreachPresenceApi';

export const outreachPresenceKeys = {
  byScenario: (scenarioId: string) => ['outreach-presence', scenarioId] as const,
};

/**
 * 이 나라 초안을 만지는 동안 작업 중 임대를 늘린다 — IA §3-7, 조사 §3-2.
 *
 * 켜지면 즉시 한 번 부르고 그 뒤 30초마다 부른다. 메일 초안 임대 훅처럼 첫 갱신을 30초 미루면
 * 그동안 다른 사람 화면에 아무것도 뜨지 않는다. 꺼지거나 화면에서 빠지면 멈추기만 하고 지우는
 * 요청은 보내지 않는다 — 임대는 2분 뒤 스스로 빠진다. 실패는 삼키고 다음 30초에 다시 시도한다.
 */
export function useOutreachPresenceLease(
  scenarioId: string,
  country: string | null,
  enabled: boolean
): void {
  useEffect(() => {
    if (!enabled || country === null) return;
    const renew = () => {
      renewOutreachPresence(scenarioId, country).catch(() => undefined);
    };
    renew();
    const interval = window.setInterval(renew, OUTREACH_PRESENCE_HEARTBEAT_MS);
    return () => window.clearInterval(interval);
  }, [scenarioId, country, enabled]);
}

/** 이 캠페인에서 지금 초안을 만지는 사람들. 임대 갱신과 같은 간격으로 다시 읽는다. */
export function useOutreachPresence(scenarioId: string, enabled = true) {
  return useQuery({
    queryKey: outreachPresenceKeys.byScenario(scenarioId),
    queryFn: () => fetchOutreachPresence(scenarioId),
    enabled,
    refetchInterval: OUTREACH_PRESENCE_HEARTBEAT_MS,
  });
}
